import { clear, createStore, del, get, keys, set } from "idb-keyval";

type StoredValue<T> = {
  value: T;
  expiresAt?: number;
};

const STORAGE_PREFIX = "vidmuse";
const DEFAULT_DB_NAME = "vidmuse-kv";
const DEFAULT_STORE_NAME = "cache";

const isBrowser = typeof window !== "undefined";
const hasIndexedDb = isBrowser && typeof window.indexedDB !== "undefined";
const hasLocalStorage = isBrowser && typeof window.localStorage !== "undefined";

const memoryStore = new Map<string, StoredValue<unknown>>();
const localStore = hasLocalStorage
  ? {
      get<T>(key: string): StoredValue<T> | null {
        try {
          const raw = window.localStorage.getItem(key);
          return raw ? (JSON.parse(raw) as StoredValue<T>) : null;
        } catch {
          window.localStorage.removeItem(key);
          return null;
        }
      },
      set<T>(key: string, value: StoredValue<T>) {
        try {
          window.localStorage.setItem(key, JSON.stringify(value));
        } catch {
          window.localStorage.removeItem(key);
        }
      },
      delete(key: string) {
        try {
          window.localStorage.removeItem(key);
        } catch {
          // ignore
        }
      },
      keys() {
        const result: string[] = [];
        try {
          for (let i = 0; i < window.localStorage.length; i += 1) {
            const k = window.localStorage.key(i);
            if (k) result.push(k);
          }
        } catch {
          return [];
        }
        return result;
      },
    }
  : null;

let store: ReturnType<typeof createStore> | null = null;
if (isBrowser && hasIndexedDb) {
  try {
    store = createStore(DEFAULT_DB_NAME, DEFAULT_STORE_NAME);
  } catch (error) {
    console.warn(
      "IndexedDB initialization failed, falling back to memory store:",
      error
    );
  }
}

const toKey = (key: string) => `${STORAGE_PREFIX}::${key}`;

function isExpired(entry?: StoredValue<unknown> | null) {
  if (!entry || !entry.expiresAt) return false;
  return Date.now() > entry.expiresAt;
}

export class StorageManager {
  async get<T>(key: string): Promise<T | null> {
    const namespaced = toKey(key);
    if (!isBrowser || !store) {
      const entry =
        localStore?.get<T>(namespaced) ??
        (memoryStore.get(namespaced) as StoredValue<T> | undefined);
      if (isExpired(entry)) {
        localStore?.delete(namespaced);
        memoryStore.delete(namespaced);
        return null;
      }
      return entry ? entry.value : null;
    }

    const entry = (await get(namespaced, store)) as StoredValue<T> | null;
    if (isExpired(entry)) {
      await del(namespaced, store);
      return null;
    }
    return entry ? entry.value : null;
  }

  async set<T>(key: string, value: T, ttlMs?: number): Promise<void> {
    const namespaced = toKey(key);
    const payload: StoredValue<T> = {
      value,
      expiresAt: ttlMs ? Date.now() + ttlMs : undefined,
    };
    if (!isBrowser || !store) {
      if (localStore) {
        localStore.set(namespaced, payload);
      } else {
        memoryStore.set(namespaced, payload);
      }
      return;
    }
    await set(namespaced, payload, store);
  }

  async remove(key: string): Promise<void> {
    const namespaced = toKey(key);
    if (!isBrowser || !store) {
      localStore?.delete(namespaced);
      memoryStore.delete(namespaced);
      return;
    }
    await del(namespaced, store);
  }

  async sweep(prefix?: string): Promise<void> {
    const filterPrefix = prefix ? toKey(prefix) : `${STORAGE_PREFIX}::`;
    if (!isBrowser || !store) {
      for (const key of Array.from(memoryStore.keys())) {
        const matchesPrefix = key.startsWith(filterPrefix);
        if (!matchesPrefix) continue;
        const entry = memoryStore.get(key);
        if (isExpired(entry)) {
          memoryStore.delete(key);
        }
      }
      if (localStore) {
        const candidateKeys = localStore
          .keys()
          .filter((k) => k.startsWith(filterPrefix));
        candidateKeys.forEach((k) => {
          const entry = localStore.get(k);
          if (isExpired(entry)) {
            localStore.delete(k);
          }
        });
      }
      return;
    }

    const allKeys = await keys(store);
    const candidates = allKeys.filter(
      (key): key is string =>
        typeof key === "string" && key.startsWith(filterPrefix)
    );

    await Promise.all(
      candidates.map(async (key) => {
        const entry = (await get(key, store!)) as StoredValue<unknown> | null;
        if (isExpired(entry)) {
          await del(key, store!);
        }
      })
    );
  }

  async clearAll(): Promise<void> {
    if (!isBrowser || !store) {
      localStore
        ?.keys()
        .filter((k) => k.startsWith(`${STORAGE_PREFIX}::`))
        .forEach((k) => localStore?.delete(k));
      memoryStore.clear();
      return;
    }
    await clear(store);
  }
}

export const storageManager = new StorageManager();
